import * as React from "react";
import { useDropzone, type DropzoneOptions } from "react-dropzone";
import { cn } from "@/lib/utils";
import { Plus, X } from "lucide-react";
import CustomImage from "@/app/components/common/Image";

interface DropzoneProps extends Omit<DropzoneOptions, "onDrop"> {
  className?: string;
  label?: string;
  // eslint-disable-next-line no-unused-vars
  onFilesChange?: (_nextFiles: Array<string | File>) => void;
  value?: Array<string | File>;
}

function Dropzone({
  className,
  label = "",
  onFilesChange,
  value = [],
  ...options
}: DropzoneProps) {
  const effectiveMaxFiles =
    typeof options.maxFiles === "number" && options.maxFiles > 0
      ? options.maxFiles
      : undefined;
  const canAddMore =
    effectiveMaxFiles === undefined || value.length < effectiveMaxFiles;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...options,
    onDrop(acceptedFiles) {
      if (canAddMore) {
        const newFiles = [...value, ...acceptedFiles];
        onFilesChange?.(newFiles.slice(0, effectiveMaxFiles));
      } else {
        onFilesChange?.(value);
      }
    },
  });

  const removeFile = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    let newFiles = [...value];
    if (newFiles.length === 1) {
      newFiles = [];
    } else {
      newFiles.splice(index, 1);
    }
    onFilesChange?.(newFiles);
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label && (
        <p className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
        </p>
      )}

      {value.length > 0 ? (
        <div className="flex flex-row grow flex-wrap gap-3">
          {value.map((preview, index) => (
            <div
              key={
                preview instanceof File ? preview.name + index : preview + index
              }
              className="relative h-28 w-28 overflow-hidden rounded-2xl border border-dashed border-muted"
            >
              <CustomImage
                src={preview}
                alt={`preview-${index}`}
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={(e) => removeFile(index, e)}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                aria-label="Remove file"
              >
                <X />
              </button>
            </div>
          ))}

          {canAddMore && (
            <div
              {...getRootProps()}
              className={cn(
                "flex h-28 w-28 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-primary transition-colors hover:border-primary/60 hover:bg-primary/5",
                isDragActive && "border-primary bg-primary/10",
              )}
            >
              <input {...getInputProps()} />
              <span className="text-2xl text-primary">+</span>
            </div>
          )}
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            "flex h-28 w-28 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-primary bg-white transition-colors hover:border-primary/60 hover:bg-primary/5",
            isDragActive && "border-primary bg-primary/10",
          )}
        >
          <input {...getInputProps()} />
          {/* <span className="text-5xl text-primary">+</span> */}
          <Plus className="text-5xl text-primary" />
          {/* {isDragActive ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Drop files here
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Click or drag & drop
            </p>
          )} */}
        </div>
      )}
    </div>
  );
}

export { Dropzone };
